import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as legalService from '../../services/legal';

export const fetchLegalSections = createAsyncThunk(
    'legal/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            return await legalService.fetchLegalSections();
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    },
    {
        condition: (_, { getState }) => getState().legal.status !== 'loading',
    }
);

export const createLegalSection = createAsyncThunk('legal/create', async (section, { rejectWithValue }) => {
    try {
        return await legalService.createLegalSection(section);
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

export const updateLegalSection = createAsyncThunk('legal/update', async ({ id, changes }, { rejectWithValue }) => {
    try {
        return await legalService.updateLegalSection(id, changes);
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

export const deleteLegalSection = createAsyncThunk('legal/delete', async (id, { rejectWithValue }) => {
    try {
        return await legalService.deleteLegalSection(id);
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

const initialState = {
    sections: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    saving: false,
    error: null,
};

// Keep the list in the order the tabs render in, so a reorder shows up without
// a refetch.
const sortSections = (sections) => sections.sort((a, b) => a.sortOrder - b.sortOrder || a.idLegalSection - b.idLegalSection);

const legalSlice = createSlice({
    name: 'legal',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLegalSections.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchLegalSections.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.sections = action.payload;
            })
            .addCase(fetchLegalSections.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(createLegalSection.fulfilled, (state, action) => {
                state.saving = false;
                state.sections = sortSections([...state.sections, action.payload]);
            })
            .addCase(updateLegalSection.fulfilled, (state, action) => {
                state.saving = false;
                const index = state.sections.findIndex((s) => s.idLegalSection === action.payload.idLegalSection);
                if (index >= 0) state.sections[index] = action.payload;
                state.sections = sortSections([...state.sections]);
            })
            .addCase(deleteLegalSection.fulfilled, (state, action) => {
                state.saving = false;
                state.sections = state.sections.filter((s) => s.idLegalSection !== action.payload.idLegalSection);
            })
            // The three writes share their pending/rejected handling.
            .addMatcher(
                (action) => action.type.startsWith('legal/') && action.type.endsWith('/pending') && !action.type.startsWith('legal/fetchAll'),
                (state) => {
                    state.saving = true;
                    state.error = null;
                }
            )
            .addMatcher(
                (action) => action.type.startsWith('legal/') && action.type.endsWith('/rejected') && !action.type.startsWith('legal/fetchAll'),
                (state, action) => {
                    state.saving = false;
                    state.error = action.payload;
                }
            );
    },
});

export const { clearError } = legalSlice.actions;

export const selectAllLegalSections = (state) => state.legal.sections;
export const selectLegalStatus = (state) => state.legal.status;
export const selectLegalSaving = (state) => state.legal.saving;
export const selectLegalError = (state) => state.legal.error;

export default legalSlice.reducer;
